import { useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import { useTranslation } from '../../../i18n/useTranslation';
import { Alert } from '../../atoms/Alert/Alert';
import { Button } from '../../atoms/Button/Button';
import { Card } from '../../atoms/Card/Card';
import { Inline } from '../../atoms/Inline/Inline';
import { LiveRegion } from '../../atoms/LiveRegion/LiveRegion';
import { PhotoUploadDropzone } from '../../atoms/PhotoUploadDropzone/PhotoUploadDropzone';
import { Stack } from '../../atoms/Stack/Stack';
import { Heading } from '../../atoms/Typography/Heading';
import { Text } from '../../atoms/Typography/Text';
import { AiUsageMeter } from '../../aiUsage/AiUsageMeter';
import { formatResetDate } from '../../aiUsage/AiLimitNotice';
import { ApiError } from '../../../lib/apiClient';
import { getAiLimitErrorKind, getAiLimitResetsAt } from '../../../services/aiUsageService';
import {
  MAX_OVERVIEW_PHOTO_MB,
  startOverviewAnalysis,
  type OverviewAnalysis,
} from '../../../services/livestockOverviewService';
import './LivestockOverview.css';

export interface OverviewUploadStepProps {
  aquariumId: string;
  /** Called once the photo is uploaded and the analysis has started. */
  onStarted: (analysis: OverviewAnalysis) => void;
}

const TIPS = ['frontal', 'light', 'whole', 'glass'] as const;

/**
 * Step 1 of "Analyse an overview photo": tips for a good photo, the honest limits, the
 * AI credit note and the upload of ONE photo of the whole tank (kept at full resolution).
 */
export function OverviewUploadStep({ aquariumId, onStarted }: OverviewUploadStepProps) {
  const { t, i18n } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  const showError = (message: string): void => {
    setError(message);
    requestAnimationFrame(() => errorRef.current?.focus());
  };

  const handleSubmit = (): void => {
    if (!file || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    startOverviewAnalysis(aquariumId, file, i18n.language)
      .then((analysis) => {
        setIsSubmitting(false);
        onStarted(analysis);
      })
      .catch((err: unknown) => {
        setIsSubmitting(false);
        const aiLimit = getAiLimitErrorKind(err);
        if (aiLimit === 'unavailable') {
          showError(t('aiUsage.unavailable'));
        } else if (aiLimit === 'exceeded') {
          const date = formatResetDate(getAiLimitResetsAt(err), i18n.language);
          showError(`${date !== null ? t('aiUsage.exceeded', { date }) : t('aiUsage.exceededNoDate')} ${t('aiUsage.supportHint')}`);
        } else if (err instanceof ApiError && (err.status === 400 || err.status === 413)) {
          showError(t('livestockOverview.upload.errorPhoto'));
        } else {
          showError(t('livestockOverview.upload.errorGeneric'));
        }
      });
  };

  return (
    <Stack gap="6">
      <Card padding="lg">
        <Stack gap="3">
          <Heading level={3}>{t('livestockOverview.upload.tipsTitle')}</Heading>
          <ul className="livestock-overview__tips">
            {TIPS.map((tip) => (
              <li key={tip}>
                <Text as="span">{t(`livestockOverview.upload.tips.${tip}`)}</Text>
              </li>
            ))}
          </ul>
          <Text color="muted">{t('livestockOverview.upload.missedHint')}</Text>
        </Stack>
      </Card>

      <Alert tone="info" live="off">
        {t('livestockOverview.upload.limits')}
      </Alert>

      <Card padding="lg">
        <Stack gap="4">
          <PhotoUploadDropzone
            onChange={(selected) => {
              setFile(selected);
              setError(null);
            }}
            maxSizeMB={MAX_OVERVIEW_PHOTO_MB}
            disabled={isSubmitting}
            ariaLabel={t('livestockOverview.upload.dropzoneLabel')}
            previewAlt={t('livestockOverview.upload.previewAlt')}
          />
          <Text size="sm" color="muted">
            {t('livestockOverview.upload.sizeHint', { size: MAX_OVERVIEW_PHOTO_MB })}
          </Text>
          <Text size="sm" color="muted">
            {t('livestockOverview.upload.credits')}
          </Text>
          <AiUsageMeter variant="compact" />

          {error && (
            <Alert ref={errorRef} tabIndex={-1} title={t('livestockOverview.upload.errorTitle')}>
              {error}
            </Alert>
          )}
          {isSubmitting && (
            <LiveRegion busy>
              <Text>{t('livestockOverview.upload.uploading')}</Text>
            </LiveRegion>
          )}

          <Inline gap="2" justify="end" wrap>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!file || isSubmitting}
              isLoading={isSubmitting}
              data-testid="overview-submit"
            >
              <Camera size={18} aria-hidden="true" />
              {t('livestockOverview.upload.submit')}
            </Button>
          </Inline>
        </Stack>
      </Card>
    </Stack>
  );
}

export default OverviewUploadStep;
